import _toConsumableArray from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _classCallCheck from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _inherits from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";
import _createSuper from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createSuper";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module list/list/listindentcommand
 */
import { Command } from 'ckeditor5/src/core.js';
import { expandListBlocksToCompleteItems, indentBlocks, isFirstBlockOfListItem, isListItemBlock, isSingleListItem, outdentBlocksWithMerge, sortBlocks, splitListItemBefore } from './utils/model.js';
import ListWalker from './utils/listwalker.js';
/**
 * The document list indent command. It is used by the {@link module:list/list~List list feature}.
 */
var ListIndentCommand = /*#__PURE__*/function (_Command) {
  _inherits(ListIndentCommand, _Command);
  var _super = _createSuper(ListIndentCommand);
  /**
   * Creates an instance of the command.
   *
   * @param editor The editor instance.
   * @param indentDirection The direction of indent. If it is equal to `backward`, the command
   * will outdent a list item.
   */
  function ListIndentCommand(editor, indentDirection) {
    var _this;
    _classCallCheck(this, ListIndentCommand);
    _this = _super.call(this, editor);
    _this._direction = indentDirection;
    return _this;
  }
  /**
   * @inheritDoc
   */
  _createClass(ListIndentCommand, [{
    key: "refresh",
    value: function refresh() {
      this.isEnabled = this._checkEnabled();
    }
    /**
     * Indents or outdents (depending on the {@link #constructor}'s `indentDirection` parameter) selected list items.
     *
     * @fires execute
     * @fires afterExecute
     */
  }, {
    key: "execute",
    value: function execute() {
      var _this2 = this;
      var model = this.editor.model;
      var blocks = getSelectedListBlocks(model.document.selection);
      model.change(function (writer) {
        var changedBlocks = [];
        // Handle selection contained in the single list item and starting in the following blocks.
        if (isSingleListItem(blocks) && !isFirstBlockOfListItem(blocks[0])) {
          // Allow increasing indent of following list item blocks.
          if (_this2._direction == 'forward') {
            changedBlocks.push.apply(changedBlocks, _toConsumableArray(indentBlocks(blocks, writer)));
          }
          // For indent make sure that indented blocks have a new ID.
          // For outdent just split blocks from the list item (give them a new IDs).
          changedBlocks.push.apply(changedBlocks, _toConsumableArray(splitListItemBefore(blocks[0], writer)));
        }
        // More than a single list item is selected, or the first block of list item is selected.
        else {
          // Now just update the attributes of blocks.
          if (_this2._direction == 'forward') {
            changedBlocks.push.apply(changedBlocks, _toConsumableArray(indentBlocks(blocks, writer, {
              expand: true
            })));
          } else {
            changedBlocks.push.apply(changedBlocks, _toConsumableArray(outdentBlocksWithMerge(blocks, writer)));
          }
        }
        // Align the list item type to match the previous list item (from the same list).
        for (var _i = 0, _changedBlocks = changedBlocks; _i < _changedBlocks.length; _i++) {
          var block = _changedBlocks[_i];
          // This block become a plain block (for example a paragraph).
          if (!block.hasAttribute('listType')) {
            continue;
          }
          var previousItemBlock = ListWalker.first(block, {
            sameIndent: true
          });
          if (previousItemBlock) {
            writer.setAttribute('listType', previousItemBlock.getAttribute('listType'), block);
          }
        }
        _this2._fireAfterExecute(changedBlocks);
      });
    }
    /**
     * Fires the `afterExecute` event.
     *
     * @param changedBlocks The changed list elements.
     */
  }, {
    key: "_fireAfterExecute",
    value: function _fireAfterExecute(changedBlocks) {
      this.fire('afterExecute', sortBlocks(new Set(changedBlocks)));
    }
    /**
     * Checks whether the command can be enabled in the current context.
     *
     * @returns Whether the command should be enabled.
     */
  }, {
    key: "_checkEnabled",
    value: function _checkEnabled() {
      // Check whether any of position's ancestor is a list item.
      var blocks = getSelectedListBlocks(this.editor.model.document.selection);
      var firstBlock = blocks[0];
      // If selection is not in a list item, the command is disabled.
      if (!firstBlock) {
        return false;
      }
      // If we are outdenting it is enough to be in list item. Every list item can always be outdented.
      if (this._direction == 'backward') {
        return true;
      }
      // A single block of a list item is selected, so it could be indented as a sublist.
      if (isSingleListItem(blocks) && !isFirstBlockOfListItem(blocks[0])) {
        return true;
      }
      blocks = expandListBlocksToCompleteItems(blocks);
      firstBlock = blocks[0];
      // Check if there is any list item before selected items that could become a parent of selected items.
      var siblingItem = ListWalker.first(firstBlock, {
        sameIndent: true
      });
      if (!siblingItem) {
        return false;
      }
      if (siblingItem.getAttribute('listType') == firstBlock.getAttribute('listType')) {
        return true;
      }
      return false;
    }
  }]);
  return ListIndentCommand;
}(Command);
/**
 * Returns an array of selected blocks truncated to the first non list block element.
 */
export { ListIndentCommand as default };
function getSelectedListBlocks(selection) {
  var blocks = Array.from(selection.getSelectedBlocks());
  var firstNonListBlockIndex = blocks.findIndex(function (block) {
    return !isListItemBlock(block);
  });
  if (firstNonListBlockIndex != -1) {
    blocks.length = firstNonListBlockIndex;
  }
  return blocks;
}